import React from "react";

/**
 * Helper function to convert an HSL color string to an HSLA string with a given alpha.
 */
const toHsla = (hsl, alpha) => {
  if (!hsl || typeof hsl !== "string" || !hsl.startsWith("hsl")) {
    // Fallback for non-HSL colors, though Rentman events should use HSL
    return "hsla(0, 0%, 0%, 0.1)";
  }
  return hsl.replace("hsl(", "hsla(").replace(")", `, ${alpha})`);
};

/**
 * [CORRECTED] Calculates the number of days between two dates for an inclusive calendar.
 * This version is more robust and correctly handles date objects from Firestore.
 */
const countDaysInclusive = (start, end) => {
  if (!start || !end) return 0;
  const msPerDay = 1000 * 60 * 60 * 24;
  
  // Create a new Date at the start of the day to avoid side effects
  const startDate = new Date(start); 
  startDate.setUTCHours(0, 0, 0, 0);
  
  const endDate = new Date(end);
  endDate.setUTCHours(0, 0, 0, 0);
  
  const diff = endDate.getTime() - startDate.getTime();
  if (diff < 0) return 0;
  return Math.round(diff / msPerDay) + 1;
};

/**
 * Safely converts a Firestore Timestamp or legacy timestamp to a Date object
 * Handles both modern Firestore Timestamps (with toDate()) and legacy format (with _seconds)
 */
const safeToDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (timestamp._seconds) {
    // Legacy Firestore v11 format
    return new Date(timestamp._seconds * 1000 + Math.floor(timestamp._nanoseconds / 1e6));
  }
  return new Date(timestamp);
};

/**
 * [DEFINITIVE VERSION]
 * Creates the custom render function for the Eventcalendar. It renders Rentman
 * events with a proportional layout and includes a safety check for malformed data.
 */
export const CreateMyScheduleEvent = (mobileShow) => {
  return (data) => {
    const ev = data.original;
    const color = ev.color || data.color;
    // Debug logging (can be removed in production)
    // console.log('🗄️ Event Renderer:', ev);
    
    // --- RENTMAN EVENT LOGIC --- //
    if (ev.rentmanEvent) {
      if (!ev.planStart || !ev.usageStart || !ev.planEnd) {
        return null;
      }

      const planStart = safeToDate(ev.planStart);
      const usageStart = safeToDate(ev.usageStart);
      const usageEnd = safeToDate(ev.usageEnd);
      const planEnd = safeToDate(ev.planEnd);

      const prepDays = countDaysInclusive(
        planStart,
        new Date(usageStart.getTime() - 1)
      );
      const opptakDays = countDaysInclusive(usageStart, usageEnd);
      const returStart = new Date(usageEnd.getTime() + 1000 * 60 * 60 * 24);
      const returDays = countDaysInclusive(returStart, planEnd);

      const totalCalculatedDays = prepDays + opptakDays + returDays;

      const prepPercent =
        totalCalculatedDays > 0 ? (prepDays / totalCalculatedDays) * 100 : 0;
      const opptakPercent =
        totalCalculatedDays > 0 ? (opptakDays / totalCalculatedDays) * 100 : 0;
      const returPercent =
        totalCalculatedDays > 0 ? (returDays / totalCalculatedDays) * 100 : 0;

      return (
        <div
          className="mbsc-schedule-event-custom"
          style={{
            display: "flex",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            borderRadius: "5px",
            border: `1px solid ${color}`,
            position: "relative",
            color: "black",
          }}
        >
          {/* This is the sticky title layer, your fix is preserved */}
          <div
            className="mbsc-schedule-event-inner"
            style={{
              padding: "3px 6px",
              fontSize: "12px",
              fontWeight: "bold",
              color: "black",
              whiteSpace: "nowrap",
              zIndex: 2,
              marginLeft: `calc(${prepPercent}% + 5px)`,
              marginRight: `calc(${returPercent}%)`,
            }}
          >
            <div className="md-timeline-template-title">
              {ev.title}
            </div>
            <div className="md-timeline-template-desc">
              {ev.parentProject && ev.parentProject.Title && ev.parentProject.Title !== ev.title
                ? `${ev.parentProject.Title} · ${opptakDays} ${opptakDays === 1 ? "day" : "days"}`
                : `${opptakDays} ${opptakDays === 1 ? "day" : "days"}`}
            </div>
          </div>

          {/* This container and its children are for the background visuals only */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              zIndex: 1,
              borderRadius: '5px', // Apply border-radius to this container
              overflow: 'hidden',   // ADDED: This is crucial to clip the inner segments
                                   // so they respect the parent's border-radius.
            }}
          >
            {/* Prep segment */}
            {prepDays > 0 && (
              <div
              className="md-timeline-template-time"
                style={{
                  width: `${prepPercent}%`,
                  backgroundColor: toHsla(color, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: `1px solid ${color}`,
                  boxSizing: 'border-box',
                }}
              >
                Prep
              </div>
            )}

            {/* Opptak segment */}
            {opptakDays > 0 && (
              <div
                style={{
                  width: `${opptakPercent}%`,
                  backgroundColor: toHsla(color, 0.2),
                  borderRight: `1px solid ${color}`,
                  boxSizing: 'border-box',
                }}
              />
            )}

            {/* Retur segment */}
            {returDays > 0 && (
              <div
              className="md-timeline-template-time"
                style={{
                  width: `${returPercent}%`,
                  backgroundColor: toHsla(color, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Retur
              </div>
            )}
          </div>
        </div>
      );
    }

    // Fallback for other event types
    // Safe date conversion for start and end
    const startDate = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endDate = ev.end instanceof Date ? ev.end : new Date(ev.end);
    
    const TotalDays = countDaysInclusive(
      startDate,
      new Date(endDate.getTime() - 1000 * 60 * 60 * 24)
    );

    // --- YOUR OTHER EVENT TYPES (STYLES RESTORED) ---
    if (ev.title === "Juleferie") {
      const ChristmasLights = () => {
        const containerRef = React.useRef(null);
        const [lightCount, setLightCount] = React.useState(Math.round(TotalDays * 1.2));
        
        React.useEffect(() => {
          const calculateLights = () => {
            if (containerRef.current) {
              const containerWidth = containerRef.current.offsetWidth;
              // Each light: 12px width + 20px margin on each side = 52px spacing between light left edges
              // First light starts at 5px (left edge), right edge at 17px
              // Last light right edge must be <= containerWidth - 5px
              // Last light left edge = 5px + (n-1) * 52px
              // Last light right edge = 5px + (n-1) * 52px + 12px <= containerWidth - 5px
              // Solving: (n-1) * 52px + 12px <= containerWidth - 10px
              // (n-1) * 52px <= containerWidth - 22px
              // n <= (containerWidth - 22px) / 52px + 1
              const maxLights = Math.floor((containerWidth - 22) / 52) + 1;
              if (maxLights > 0) {
                setLightCount(maxLights);
              } else {
                setLightCount(1); // At least show one light
              }
            }
          };
          
          calculateLights();
          const resizeObserver = new ResizeObserver(calculateLights);
          if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
          }
          
          return () => resizeObserver.disconnect();
        }, []);
        
        return (
          <div
            ref={containerRef}
            className={`christmas md-timeline-template-event md-timeline-template-event-christemas ${
              mobileShow ? "" : "mobile-toggle"
            }`}
          >
            <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
              <div id="flex-event-expand">
                <span className="md-timeline-template-title-christemas">
                  PRIVAT: {ev.title} - {TotalDays} days
                </span>
                <span className="md-timeline-template-desc-christemas">
                  {ev.description}
                </span>
              </div>
            </div>
            <ul className="strand">
              {Array.from(Array(lightCount), (e, i) => (
                <li key={i}></li>
              ))}
            </ul>
          </div>
        );
      };
      
      return <ChristmasLights />;
    }

    if (ev.busy === true) {
      return (
        <div
          className={`md-timeline-template-event md-timeline-template-event-busy ${
            mobileShow ? "" : "mobile-toggle"
          }`}
        >
          <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
            <div id="flex-event-expand">
              <span className="md-timeline-template-title-busy">
                PRIVAT: {ev.title} - {TotalDays} days
              </span>
              <span className="md-timeline-template-desc-busy">
                {ev.description}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (!ev.editable) {
      return (
        <div
          className={`md-timeline-template-event disabled-event ${
            mobileShow ? "" : "mobile-toggle"
          }`}
          style={{
            borderColor: `${color}${Math.floor(1 * 255)
              .toString(16)
              .padStart(2, "0")}`,
            background: `${color}${Math.floor(0.4 * 255)
              .toString(16)
              .padStart(2, "0")}`,
          }}
        >
          <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
            <div id="flex-event-expand">
              <span className="md-timeline-template-title">
                {ev.title} - {TotalDays} days{" "}
              </span>
              <span className="md-timeline-template-desc">
                {ev.description}
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Fallback for other standard events
    return (
      <div
        className={`md-timeline-template-event ${
          mobileShow ? "" : "mobile-toggle"
        }`}
        style={{
          borderColor: `${color}${Math.floor(1 * 255)
            .toString(16)
            .padStart(2, "0")}`,
          background: `${color}${Math.floor(0.4 * 255)
            .toString(16)
            .padStart(2, "0")}`,
        }}
      >
        <div className="mbsc-schedule-event-inner md-timeline-template-event-cont-expand">
          <div id="flex-event-expand">
            <span className="md-timeline-template-title">
              {ev.title} - {TotalDays} days
            </span>
            <span className="md-timeline-template-desc">{ev.description}</span>
          </div>
        </div>
      </div>
    );
  };
};
